import { generateFixedColorGradient } from '@/colors';
import { IThemeContext } from '../../interfaces/types';
import { IAgreement } from '../../interfaces/types';

enum BookstoreType {
  BOOK,
  MAGAZINE,
  AUDIOBOOK,
  MULTIPLE,
}

export function mixArrays<T>(...arrays: T[][]): T[][] {
  const mixedArr: T[][] = [];
  const maxSize = Math.max(...arrays.map((a) => a.length));

  for (let i = 0; i <= maxSize; i++) {
    arrays.forEach((arr: T[]) => {
      const el: T[] = arr.pop() as T[];

      if (el) mixedArr.push(el);
    });
  }
  return mixedArr;
}

export interface BookstoreLabels {
  singular: string;
  plural: string;
  prefixedSingular: string;
  prefixedPlural: string;
  prefix: string;
  prefixPlural: string;
}

function getBookstoreType(bookstoreTypes: string[]): BookstoreType {
  if (bookstoreTypes.length > 1) return BookstoreType.MULTIPLE;

  if (bookstoreTypes[0] === 'book') return BookstoreType.BOOK;

  if (bookstoreTypes[0] === 'audiobook') return BookstoreType.AUDIOBOOK;

  return BookstoreType.MAGAZINE;
}

function getBookstoreLabels(bookstoreType: BookstoreType): BookstoreLabels {
  let labels: BookstoreLabels;

  if (bookstoreType === BookstoreType.BOOK)
    labels = {
      singular: 'Livro',
      plural: 'Livros',
      prefix: 'o',
      prefixPlural: 'os',
      prefixedSingular: 'o Livro',
      prefixedPlural: 'os Livros',
    };
  else if (bookstoreType === BookstoreType.MAGAZINE)
    labels = {
      singular: 'Revista',
      plural: 'Revistas',
      prefix: 'a',
      prefixPlural: 'as',
      prefixedSingular: 'a Revista',
      prefixedPlural: 'as Revistas',
    };
  else if (bookstoreType === BookstoreType.AUDIOBOOK)
    labels = {
      singular: 'Audiolivro',
      plural: 'Audiolivros',
      prefix: 'o',
      prefixPlural: 'os',
      prefixedSingular: 'o Audiolivro',
      prefixedPlural: 'os Audiolivros',
    };
  else
    labels = {
      singular: 'Acervo',
      plural: 'Acervo',
      prefix: 'o',
      prefixPlural: 'os',
      prefixedSingular: 'o acervo',
      prefixedPlural: 'os acervo',
    };

  labels.prefixPlural = labels.prefix + 's';
  labels.prefixedPlural = labels.prefixPlural + ' ' + labels.plural;
  labels.prefixedSingular = labels.prefix + ' ' + labels.singular;

  return labels;
}

export function parseAgreementToBookstore(agreement: IAgreement): IThemeContext {
  const bookstoreType = getBookstoreType(agreement.bookstoreTypes);
  const bookstoreLabels = getBookstoreLabels(bookstoreType);

  return {
    ...agreement,
    colors: {
      primaryColor: agreement.primaryColor,
      secondaryColor: agreement.secondaryColor,
      menuColor: agreement.menuColor,
      menuFontColor: agreement.menuFontColor,
      primaryFontColor: agreement.primaryFontColor,
      secondaryFontColor: agreement.secondaryFontColor,
      graphColors: mixArrays(
        generateFixedColorGradient(agreement.primaryColor, 30),
        generateFixedColorGradient(agreement.secondaryColor, 20)
      ),
    },
    bookstoreLabels: bookstoreLabels,
    isNative: false,
    childrenList: agreement.children,
    recurringBook: true,
    telephone: agreement.telephone || '',
    isSetup: false,
    privacyPolicy: '',
    legalText: '',
    openModalLogin: () => {},
    isMenuOpen: false,
    isApp: false,
    isIOS: false,
    fetchBenefits: () => {},
    setLoading: () => {},
    setMenuOpen: () => {},
    updateTheme: () => {},
    resetTheme: () => {},
    getValidSubscriptions: () => [],
    benefits: [],
    children: [],
  };
}

export function parseAgreementToTheme(agreement: IAgreement): IThemeContext {
  return {
    ...agreement,
    colors: {
      primaryColor: agreement.primaryColor,
      secondaryColor: agreement.secondaryColor,
      menuColor: agreement.menuColor,
      menuFontColor: agreement.menuFontColor,
      primaryFontColor: '#747474',
      secondaryFontColor: agreement.secondaryFontColor,
    },
    children: null,
    isNative: false,
    childrenList: agreement.children,
    recurringBook: true,
    telephone: agreement.telephone || '',
    isSetup: false,
    privacyPolicy: '',
    legalText: '',
    openModalLogin: () => {},
    isMenuOpen: false,
    isApp: false,
    isIOS: false,
    fetchBenefits: () => {},
    setLoading: () => {},
    setMenuOpen: () => {},
    updateTheme: () => {},
    resetTheme: () => {},
    getValidSubscriptions: () => [],
    benefits: [],
    bookstoreLabels: null,
  };
}
